import React from 'react';
import {
  BLADE_PATHS,
  COLLAPSE_END_MS,
  EASE_COLLAPSE,
  EASE_DIM,
  EASE_SPROUT,
  EASE_SWEEP,
  LOGO_BLADES,
  LOGO_CANVAS_PX,
  LOGO_DOTS,
  LOGO_DURATION_MS,
  ROTATION_FROM_DEG,
  ROTATION_PIVOT_X,
  ROTATION_PIVOT_Y,
  SPROUT_START_MS,
  SWEEP_END_MS,
  SWEEP_START_MS,
  type LogoBlade,
  type LogoDot,
} from './assistantMark.data';

// Rotation, dot growth and dim share one window, so they share one duration.
const SWEEP_MS = SWEEP_END_MS - SWEEP_START_MS;
const COLLAPSE_MS = COLLAPSE_END_MS - SWEEP_START_MS;
const SPROUT_MS = SWEEP_END_MS - SPROUT_START_MS;

/**
 * Sprout animates `scale` while slide animates `translate` on the same element,
 * which only composes because these are the independent transform properties
 * and not one `transform` shorthand.
 */
const MARK_ANIMATION_STYLE = `
@keyframes aa-mark-pop{from{opacity:0}to{opacity:1}}
@keyframes aa-mark-settle{from{opacity:1}to{opacity:var(--aa-end-opacity,1)}}
@keyframes aa-mark-sweep{from{scale:var(--aa-from-scale,1);translate:var(--aa-from-x,0px) var(--aa-from-y,0px)}to{scale:var(--aa-to-scale,1);translate:var(--aa-to-x,0px) var(--aa-to-y,0px)}}
@keyframes aa-mark-collapse{from{scale:1}to{scale:var(--aa-to-scale,1)}}
@keyframes aa-mark-sprout{from{scale:var(--aa-from-scale-x,1) var(--aa-from-scale-y,1)}to{scale:1 1}}
@keyframes aa-mark-slide{from{translate:var(--aa-from-x,0px) var(--aa-from-y,0px)}50%{translate:var(--aa-mid-x,0px) var(--aa-mid-y,0px);animation-timing-function:var(--aa-slide-ease,linear)}to{translate:var(--aa-to-x,0px) var(--aa-to-y,0px)}}
@keyframes aa-mark-turn{from{rotate:${ROTATION_FROM_DEG}deg}to{rotate:0deg}}
.aa-mark-rotator{position:absolute;top:0;left:0;width:100%;height:100%;transform-origin:${ROTATION_PIVOT_X}px ${ROTATION_PIVOT_Y}px;animation:aa-mark-turn ${SWEEP_MS}ms ${EASE_SWEEP} ${SWEEP_START_MS}ms both}
.aa-mark-dot{position:absolute;border-radius:50%;background-color:currentColor;animation:aa-mark-pop var(--aa-pop-ms) steps(1,end) forwards,aa-mark-settle ${SWEEP_MS}ms ${EASE_DIM} ${SWEEP_START_MS}ms forwards}
.aa-mark-dot--sweep{animation:aa-mark-pop var(--aa-pop-ms) steps(1,end) forwards,aa-mark-settle ${SWEEP_MS}ms ${EASE_DIM} ${SWEEP_START_MS}ms forwards,aa-mark-sweep ${SWEEP_MS}ms ${EASE_SWEEP} ${SWEEP_START_MS}ms both}
.aa-mark-dot--collapse{animation:aa-mark-pop var(--aa-pop-ms) steps(1,end) forwards,aa-mark-settle ${SWEEP_MS}ms ${EASE_DIM} ${SWEEP_START_MS}ms forwards,aa-mark-collapse ${COLLAPSE_MS}ms ${EASE_COLLAPSE} ${SWEEP_START_MS}ms forwards}
.aa-mark-blade{position:absolute;animation:aa-mark-pop ${SPROUT_START_MS}ms steps(1,end) forwards,aa-mark-sprout ${SPROUT_MS}ms ${EASE_SPROUT} ${SPROUT_START_MS}ms both,aa-mark-slide ${SPROUT_MS}ms linear ${SPROUT_START_MS}ms both}
.aa-mark-blade svg{display:block;width:100%;height:100%}
@media (prefers-reduced-motion:reduce){
.aa-mark-rotator{animation:none;rotate:0deg}
.aa-mark-dot,.aa-mark-blade{animation:none;opacity:var(--aa-end-opacity,1);scale:var(--aa-to-scale,1);translate:var(--aa-to-x,0px) var(--aa-to-y,0px)}
}`;

const dotStyle = (dot: LogoDot): React.CSSProperties =>
  ({
    left: `${dot.left}px`,
    top: `${dot.top}px`,
    width: `${dot.size}px`,
    height: `${dot.size}px`,
    '--aa-pop-ms': `${Math.round(dot.popAt * LOGO_DURATION_MS)}ms`,
    '--aa-end-opacity': dot.endOpacity,
    ...(dot.sweep && {
      '--aa-from-scale': dot.sweep.from,
      '--aa-to-scale': dot.sweep.to,
      '--aa-from-x': `${dot.sweep.fromX}px`,
      '--aa-from-y': `${dot.sweep.fromY}px`,
      '--aa-to-x': `${dot.sweep.toX}px`,
      '--aa-to-y': `${dot.sweep.toY}px`,
    }),
    ...(dot.collapseTo !== undefined && { '--aa-to-scale': dot.collapseTo }),
  } as React.CSSProperties);

const bladeStyle = (blade: LogoBlade): React.CSSProperties =>
  ({
    left: `${blade.left}px`,
    top: `${blade.top}px`,
    width: `${blade.width}px`,
    height: `${blade.height}px`,
    '--aa-from-scale-x': blade.fromScaleX,
    '--aa-from-scale-y': blade.fromScaleY,
    '--aa-from-x': `${blade.fromX}px`,
    '--aa-from-y': `${blade.fromY}px`,
    '--aa-mid-x': `${blade.midX}px`,
    '--aa-mid-y': `${blade.midY}px`,
    '--aa-to-x': `${blade.toX}px`,
    '--aa-to-y': `${blade.toY}px`,
    '--aa-slide-ease': blade.slideEase,
  } as React.CSSProperties);

const dotClassName = (dot: LogoDot) => {
  if (dot.sweep) return 'aa-mark-dot aa-mark-dot--sweep';
  if (dot.collapseTo !== undefined) return 'aa-mark-dot aa-mark-dot--collapse';
  return 'aa-mark-dot';
};

export type AnimatedAssistantIconProps = {
  /** Rendered edge length in px. The 280px canvas scales to fit. */
  size?: number;
  /** Defaults to the inherited ink; pass `#191BE5` for Tyto's brand blue. */
  color?: string;
  className?: string;
};

/**
 * The mark assembling itself, once per mount over 2s. Holds the frame
 * `AssistantIcon` draws, so the two can sit side by side.
 */
const AnimatedAssistantIcon: React.FC<AnimatedAssistantIconProps> = ({
  size = 64,
  color,
  className,
}) => (
  <div
    className={className}
    style={{
      position: 'relative',
      flexShrink: 0,
      width: size,
      height: size,
      ...(color ? { color } : null),
    }}
    aria-hidden>
    <style>{MARK_ANIMATION_STYLE}</style>
    {/* Holds the authored 280px canvas and scales it down to fit. */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: LOGO_CANVAS_PX,
        height: LOGO_CANVAS_PX,
        transform: `scale(${size / LOGO_CANVAS_PX})`,
        transformOrigin: '0 0',
      }}>
      <div className="aa-mark-rotator">
        {/* Blades paint below the dots, matching the Figma layer order. */}
        {LOGO_BLADES.map(blade => (
          <div
            key={blade.node}
            className="aa-mark-blade"
            style={bladeStyle(blade)}>
            <svg
              viewBox={`0 0 ${blade.width} ${blade.height}`}
              preserveAspectRatio="none"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path d={BLADE_PATHS[blade.slug]} fill="currentColor" />
            </svg>
          </div>
        ))}
        {LOGO_DOTS.map(dot => (
          <div
            key={dot.node}
            className={dotClassName(dot)}
            style={dotStyle(dot)}
          />
        ))}
      </div>
    </div>
  </div>
);

export default AnimatedAssistantIcon;
